import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import ButtonFloat from '@/domains/mobile-web/components/ButtonFloat/ButtonFloat';
import CardDonationPost from '@/domains/mobile-web/components/CardDonationPost/CardDonationPost';
import TabMobile, { type TabItem } from '@/domains/mobile-web/components/TabMobile/TabMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import {
  useBookRequestComments,
  useBookRequestPages,
  useDeleteBookRequest,
  useDeleteBookRequestComment,
  useLikeBookRequest,
  useLikeBookRequestComment,
} from '@/domains/mobile-web/hooks/useBookRequests';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import type { BookRequest, BookRequestFeed } from '@/domains/mobile-web/types/bookRequest.types';
import { toDonationPost } from '@/domains/mobile-web/utils/bookRequest';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

const TABS: TabItem<BookRequestFeed>[] = [
  { value: 'timeline', label: 'Timeline' },
  { value: 'mine', label: 'Postingan Saya' },
];

/** What the open delete confirmation is about */
type PendingDelete = { type: 'post' } | { type: 'comment'; commentId: string };

/** One post; its comments are loaded when the comment count is tapped. */
const DonationPostItem = ({ row }: { row: BookRequest }) => {
  const navigate = useNavigate();
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const comments = useBookRequestComments(row.id, commentsOpen && row.comment_count > 0);
  const likePost = useLikeBookRequest();
  const deletePost = useDeleteBookRequest();
  const likeComment = useLikeBookRequestComment();
  const deleteComment = useDeleteBookRequestComment();

  const commentRows = comments.data?.pages.flatMap((page) => page.rows) ?? [];
  const post = toDonationPost(row, commentRows);
  const commentsNote = comments.isError
    ? 'Gagal memuat komentar.'
    : commentsOpen && row.comment_count > 0 && comments.isLoading
      ? 'Memuat komentar...'
      : undefined;

  const closeDelete = () => setPendingDelete(null);
  const confirmDelete = () => {
    if (pendingDelete?.type === 'post') {
      deletePost.mutate(row.id, { onSettled: closeDelete });
    } else if (pendingDelete?.type === 'comment') {
      deleteComment.mutate(
        { postId: row.id, commentId: pendingDelete.commentId },
        { onSettled: closeDelete }
      );
    }
  };

  return (
    <>
      <CardDonationPost
        post={post}
        showComments={commentsOpen}
        commentsNote={commentsNote}
        onOpen={() => navigate(`/store/request/${row.id}`, { state: { post: row } })}
        onToggleComments={() => setCommentsOpen((open) => !open)}
        onLike={() => likePost.mutate({ postId: row.id, liked: !row.liked_by_me })}
        onReply={() =>
          navigate(`/store/reply/${row.id}`, { state: { from: 'donation', post: row } })
        }
        onDelete={row.is_mine ? () => setPendingDelete({ type: 'post' }) : undefined}
        onLikeComment={(comment) =>
          likeComment.mutate({ postId: row.id, commentId: comment.id, liked: !comment.isLiked })
        }
        onDeleteComment={(comment) => setPendingDelete({ type: 'comment', commentId: comment.id })}
      />
      <ConfirmModal
        open={pendingDelete !== null}
        title={pendingDelete?.type === 'comment' ? 'Hapus Komentar ?' : 'Hapus Postingan ?'}
        message={
          pendingDelete?.type === 'comment'
            ? 'Apakah Anda yakin ingin menghapus komentar ini?'
            : 'Apakah Anda yakin ingin menghapus postingan ini?'
        }
        confirmLabel="Ya, Hapus"
        variant="danger"
        loading={deletePost.isPending || deleteComment.isPending}
        onConfirm={confirmDelete}
        onClose={closeDelete}
      />
    </>
  );
};

const Donation = () => {
  const navigate = useNavigate();
  const cartCount = useCartCount();
  const [activeTab, setActiveTab] = useState<BookRequestFeed>('timeline');

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useBookRequestPages(activeTab);
  const rows = data?.pages.flatMap((page) => page.rows) ?? [];

  // Loads the next page when the end of the list scrolls into view
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <main className="min-h-screen relative">
      <HeaderMobile>
        <TopBarHeader
          type="textBucket"
          totalProduct={cartCount}
          title="Donasi Buku"
          onAction={() => navigate('/store/cart')}
        />
        <TabMobile items={TABS} value={activeTab} onChange={setActiveTab} />
      </HeaderMobile>

      <WrapperMobile className="mb-[60px] pt-3 relative">
        {isLoading ? (
          <p className="reg12 text-text-grey text-center py-6">Memuat postingan...</p>
        ) : isError ? (
          <p className="reg12 text-text-grey text-center py-6">Gagal memuat postingan.</p>
        ) : rows.length === 0 ? (
          <p className="reg12 text-text-grey text-center py-6">Belum ada postingan.</p>
        ) : (
          <>
            {rows.map((row) => (
              <DonationPostItem key={row.id} row={row} />
            ))}

            {hasNextPage && <div ref={sentinelRef} aria-hidden="true" />}
          </>
        )}

        <ButtonFloat onClick={() => navigate(`/store/post`)} />
      </WrapperMobile>
    </main>
  );
};

export default Donation;
